import type { Sequence, Contact, ConversationMessage, DashboardMetrics } from "./types";

export const sequences: Sequence[] = [
	{
		id: "seq-1",
		name: "Enterprise Outbound Q1",
		description: "Multi-touch sequence targeting VP+ at Fortune 500 companies",
		status: "active",
		contactCount: 342,
		steps: [
			{ id: "s1", channel: "email", subject: "Quick question about {{company}}", body: "Hi {{firstName}},\n\nI noticed {{company}} recently expanded into new markets. We help companies like yours streamline their outbound sales process with AI-powered sequencing.\n\nWould you be open to a 15-minute call this week?", delayDays: 0, order: 1 },
			{ id: "s2", channel: "linkedin", body: "Hi {{firstName}}, I sent you a quick note about how we're helping companies like {{company}} improve their sales outreach. Would love to connect!", delayDays: 2, order: 2 },
			{ id: "s3", channel: "email", subject: "Re: Quick question about {{company}}", body: "Hi {{firstName}},\n\nJust following up on my previous email. I wanted to share a case study showing how we helped a similar company increase their reply rates by 3x.\n\nWorth a quick chat?", delayDays: 4, order: 3 },
			{ id: "s4", channel: "sms", body: "Hi {{firstName}}, this is Alex from SequenceAI. Sent you an email about improving outbound at {{company}}. Happy to jump on a quick call if you're interested!", delayDays: 7, order: 4 },
			{ id: "s5", channel: "email", subject: "Last note from me", body: "Hi {{firstName}},\n\nI don't want to be a pest, so this will be my last reach out. If improving your team's sales engagement is ever a priority, I'd love to help.\n\nAll the best!", delayDays: 10, order: 5 },
		],
		metrics: { sent: 1420, opened: 823, replied: 198, bounced: 34 },
		createdAt: "2026-01-15",
		updatedAt: "2026-02-10",
	},
	{
		id: "seq-2",
		name: "SMB Product Demo",
		description: "Warm lead nurture sequence for demo requests",
		status: "active",
		contactCount: 189,
		steps: [
			{ id: "s6", channel: "email", subject: "Your demo of SequenceAI", body: "Hi {{firstName}},\n\nThanks for requesting a demo! I'd love to show you how SequenceAI can help {{company}} automate multi-channel outreach.\n\nHere's my calendar link — pick a time that works for you.", delayDays: 0, order: 1 },
			{ id: "s7", channel: "email", subject: "Still interested in a demo?", body: "Hi {{firstName}},\n\nJust checking in — did you get a chance to book your demo? Happy to answer any questions beforehand.", delayDays: 3, order: 2 },
			{ id: "s8", channel: "sms", body: "Hey {{firstName}}! Quick reminder about your SequenceAI demo. Want me to send over some times?", delayDays: 5, order: 3 },
		],
		metrics: { sent: 567, opened: 389, replied: 112, bounced: 8 },
		createdAt: "2026-01-22",
		updatedAt: "2026-02-08",
	},
	{
		id: "seq-3",
		name: "Re-engagement Campaign",
		description: "Win back churned accounts with personalized outreach",
		status: "paused",
		contactCount: 78,
		steps: [
			{ id: "s9", channel: "email", subject: "We miss you, {{firstName}}", body: "Hi {{firstName}},\n\nIt's been a while since we last spoke. We've made some incredible improvements since then, including AI-powered message writing and advanced analytics.\n\nWould you like to see what's new?", delayDays: 0, order: 1 },
			{ id: "s10", channel: "linkedin", body: "Hey {{firstName}}, hope you're doing well at {{company}}! We've shipped some great new features. Would love to reconnect.", delayDays: 3, order: 2 },
		],
		metrics: { sent: 156, opened: 67, replied: 23, bounced: 5 },
		createdAt: "2026-02-01",
		updatedAt: "2026-02-05",
	},
	{
		id: "seq-4",
		name: "Conference Follow-up",
		description: "Follow up with leads from SaaStr 2026",
		status: "draft",
		contactCount: 0,
		steps: [
			{ id: "s11", channel: "email", subject: "Great meeting you at SaaStr!", body: "Hi {{firstName}},\n\nIt was great connecting at SaaStr this year! As we discussed, SequenceAI can help {{company}} scale personalized outreach.\n\nLet's continue the conversation?", delayDays: 0, order: 1 },
		],
		metrics: { sent: 0, opened: 0, replied: 0, bounced: 0 },
		createdAt: "2026-02-10",
		updatedAt: "2026-02-10",
	},
];

export const contacts: Contact[] = [
	{ id: "c1", name: "Sarah Chen", email: "sarah.chen@techcorp.com", phone: "+1 415-555-0142", company: "TechCorp", title: "VP of Sales", linkedinUrl: "linkedin.com/in/sarachen", status: "active", sequenceId: "seq-1", currentStep: 3, lastContactedAt: "2026-02-09" },
	{ id: "c2", name: "Marcus Johnson", email: "m.johnson@growthio.com", phone: "+1 212-555-0198", company: "Growth.io", title: "Head of Revenue", status: "replied", sequenceId: "seq-1", currentStep: 2, lastContactedAt: "2026-02-08" },
	{ id: "c3", name: "Emily Nakamura", email: "emily@startuplab.co", company: "StartupLab", title: "CEO", status: "active", sequenceId: "seq-2", currentStep: 1, lastContactedAt: "2026-02-10" },
	{ id: "c4", name: "David Park", email: "dpark@enterprise.com", phone: "+1 650-555-0177", company: "Enterprise Inc", title: "Director of Sales Ops", linkedinUrl: "linkedin.com/in/davidpark", status: "active", sequenceId: "seq-1", currentStep: 4, lastContactedAt: "2026-02-07" },
	{ id: "c5", name: "Lisa Fernandez", email: "lfernandez@scaleup.io", company: "ScaleUp", title: "CRO", status: "bounced", sequenceId: "seq-1", lastContactedAt: "2026-02-03" },
	{ id: "c6", name: "James O'Brien", email: "james@cloudnative.dev", phone: "+1 310-555-0165", company: "CloudNative", title: "VP Engineering", status: "active", sequenceId: "seq-2", currentStep: 2, lastContactedAt: "2026-02-09" },
	{ id: "c7", name: "Aisha Patel", email: "aisha.p@innovate.co", company: "Innovate Co", title: "Head of Growth", linkedinUrl: "linkedin.com/in/aishapatel", status: "replied", sequenceId: "seq-2", currentStep: 3, lastContactedAt: "2026-02-06" },
	{ id: "c8", name: "Robert Kim", email: "rkim@megacorp.com", phone: "+1 408-555-0133", company: "MegaCorp", title: "Sales Director", status: "opted-out", sequenceId: "seq-3", lastContactedAt: "2026-02-01" },
	{ id: "c9", name: "Nina Volkov", email: "nina.v@datadrive.io", company: "DataDrive", title: "VP Revenue", status: "active", sequenceId: "seq-1", currentStep: 1, lastContactedAt: "2026-02-10" },
	{ id: "c10", name: "Tom Bradley", email: "tbradley@saasify.com", phone: "+1 512-555-0188", company: "SaaSify", title: "CEO", linkedinUrl: "linkedin.com/in/tombradley", status: "active", sequenceId: "seq-2", currentStep: 1, lastContactedAt: "2026-02-10" },
];

export const conversations: ConversationMessage[] = [
	{ id: "m1", contactId: "c2", channel: "email", direction: "outbound", content: "Hi Marcus,\n\nI noticed Growth.io recently expanded into new markets. We help companies like yours streamline their outbound sales process with AI-powered sequencing.\n\nWould you be open to a 15-minute call this week?", timestamp: "2026-02-05T10:00:00Z" },
	{ id: "m2", contactId: "c2", channel: "linkedin", direction: "outbound", content: "Hi Marcus, I sent you a quick note about how we're helping companies like Growth.io improve their sales outreach. Would love to connect!", timestamp: "2026-02-07T14:30:00Z" },
	{ id: "m3", contactId: "c2", channel: "email", direction: "inbound", content: "Hey, thanks for reaching out. We've actually been looking at tools to improve our outbound motion. Can you tell me more about pricing and how it integrates with HubSpot?", timestamp: "2026-02-08T09:15:00Z" },
	{ id: "m4", contactId: "c7", channel: "email", direction: "outbound", content: "Hi Aisha,\n\nThanks for requesting a demo! I'd love to show you how SequenceAI can help Innovate Co automate multi-channel outreach.\n\nHere's my calendar link — pick a time that works for you.", timestamp: "2026-02-03T11:00:00Z" },
	{ id: "m5", contactId: "c7", channel: "email", direction: "outbound", content: "Hi Aisha,\n\nJust checking in — did you get a chance to book your demo? Happy to answer any questions beforehand.", timestamp: "2026-02-06T10:00:00Z" },
	{ id: "m6", contactId: "c7", channel: "email", direction: "inbound", content: "Hi! Yes, I'd love to see a demo. I'm particularly interested in the AI writing features and LinkedIn automation. Does Thursday at 2pm work?", timestamp: "2026-02-06T16:45:00Z" },
];

export const dashboardMetrics: DashboardMetrics = {
	totalSent: 2143,
	totalOpened: 1279,
	totalReplied: 333,
	totalBounced: 47,
	openRate: 59.7,
	replyRate: 15.5,
	bounceRate: 2.2,
	dailyActivity: [
		{ date: "Feb 4", sent: 145, opened: 82, replied: 18 },
		{ date: "Feb 5", sent: 168, opened: 95, replied: 24 },
		{ date: "Feb 6", sent: 132, opened: 78, replied: 21 },
		{ date: "Feb 7", sent: 189, opened: 112, replied: 32 },
		{ date: "Feb 8", sent: 156, opened: 98, replied: 28 },
		{ date: "Feb 9", sent: 201, opened: 119, replied: 35 },
		{ date: "Feb 10", sent: 178, opened: 105, replied: 29 },
		{ date: "Feb 11", sent: 195, opened: 115, replied: 31 },
		{ date: "Feb 12", sent: 210, opened: 128, replied: 38 },
		{ date: "Feb 13", sent: 187, opened: 108, replied: 30 },
	],
	channelBreakdown: [
		{ channel: "email", sent: 1520, replied: 245 },
		{ channel: "linkedin", sent: 412, replied: 62 },
		{ channel: "sms", sent: 211, replied: 26 },
	],
	topSequences: [
		{ name: "SMB Product Demo", replyRate: 19.8, sent: 567 },
		{ name: "Enterprise Outbound Q1", replyRate: 13.9, sent: 1420 },
		{ name: "Re-engagement Campaign", replyRate: 14.7, sent: 156 },
	],
};
